import React from 'react';
import { SectionCard } from './SectionCard';
import { formatDate } from '../../utils/dateFormat';

// What the certificate reader saw at submission, next to what was declared
// (docs/modules/workflow-ld-cpd.md §7). Rendered on the declaration page and
// in HR's spot-check modal; a hand-typed declaration has nothing to compare
// against and renders nothing.
//
// The point is the mismatches: a title the submitter rewrote, hours that grew,
// or a certificate issued to somebody else. Declared values stay authoritative
// — this is evidence for the person checking, not a validation rule.
const READ_FIELDS = [
  { key: 'trainingTitle', label: 'Course / Seminar' },
  { key: 'certificateDate', label: 'Certificate Date', fmt: formatDate },
  { key: 'hours', label: 'Hours' },
];

const sameValue = (a, b) => {
  if (a == null || b == null) return false;
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b);
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
};

/**
 *   extracted — the declaration's `extracted` (null → renders nothing)
 *   declared  — { trainingTitle, certificateDate, hours }
 *   staffName — whose declaration it is (for the name line)
 *   bare      — render the table without the SectionCard frame (inside a modal)
 */
const CertificateReadingCard = ({ extracted, declared, staffName, bare = false }) => {
  const read = extracted;
  if (!read) return null;

  const nameMismatch = read.nameMatch && read.nameMatch.matches === false;

  const body = (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="text-left font-medium py-2 pr-4">Field</th>
              <th className="text-left font-medium py-2 pr-4">Read from Certificate</th>
              <th className="text-left font-medium py-2">Declared</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {READ_FIELDS.map((f) => {
              const readValue = read[f.key];
              const declaredValue = declared ? declared[f.key] : null;
              const differs = readValue != null && !sameValue(readValue, declaredValue);
              const show = (v) => (v == null || v === '' ? '—' : (f.fmt ? f.fmt(v) : String(v)));
              return (
                <tr key={f.key} className={differs ? 'bg-amber-50' : undefined}>
                  <td className="py-2 pr-4 text-slate-600">{f.label}</td>
                  <td className="py-2 pr-4 text-slate-900">
                    {show(readValue)}
                    {readValue == null && <span className="text-xs text-slate-500 ml-1">(not readable)</span>}
                  </td>
                  <td className={`py-2 ${differs ? 'font-medium text-amber-900' : 'text-slate-900'}`}>
                    {show(declaredValue)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {read.participantName && (
        <div className={`mt-3 p-3 rounded-lg text-sm ${
          nameMismatch
            ? 'bg-amber-50 border border-amber-200 text-amber-800'
            : 'bg-slate-50 border border-slate-200 text-slate-600'
        }`}>
          <span>
            Issued to “{read.participantName}”
            {nameMismatch
              ? ` — this does not look like ${staffName || 'the declarant'}. Check the certificate.`
              : '.'}
          </span>
        </div>
      )}
    </>
  );

  if (bare) return body;
  return <SectionCard title="What the Certificate Says">{body}</SectionCard>;
};

export default CertificateReadingCard;
